import React from 'react';
import { cn } from '../../lib/utils';

interface ResponsiveTextProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'display-lg' | 'display-md' | 'display-sm' | 'heading-lg' | 'heading-md' | 'heading-sm' | 'body-lg' | 'body-md' | 'body-sm' | 'caption';
  responsive?: boolean;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  align?: 'left' | 'center' | 'right' | 'justify';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'muted' | 'white';
}

export const ResponsiveText: React.FC<ResponsiveTextProps> = ({
  children,
  className,
  variant = 'body-md',
  responsive = true,
  as: Component = 'div',
  weight = 'normal',
  align = 'left',
  color = 'white',
}) => {
  const variantClasses = {
    'display-lg': responsive ? 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl' : 'text-display-lg',
    'display-md': responsive ? 'text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl' : 'text-display-md',
    'display-sm': responsive ? 'text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl' : 'text-display-sm',
    'heading-lg': responsive ? 'text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl' : 'text-heading-lg',
    'heading-md': responsive ? 'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl' : 'text-heading-md',
    'heading-sm': responsive ? 'text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl' : 'text-heading-sm',
    'body-lg': responsive ? 'text-sm sm:text-base md:text-lg lg:text-xl' : 'text-body-lg',
    'body-md': responsive ? 'text-xs sm:text-sm md:text-base lg:text-lg' : 'text-body-md',
    'body-sm': responsive ? 'text-xs sm:text-sm md:text-base' : 'text-body-sm',
    'caption': responsive ? 'text-xs' : 'text-caption',
  };

  const weightClasses = {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
  };

  const colorClasses = {
    primary: 'text-text-enterprise-primary',
    secondary: 'text-text-enterprise-secondary',
    success: 'text-success-500',
    warning: 'text-warning-500',
    error: 'text-error-500',
    muted: 'text-text-muted',
    white: 'text-white',
  };

  return (
    <Component
      className={cn(
        variantClasses[variant],
        weightClasses[weight],
        alignClasses[align],
        colorClasses[color],
        'leading-tight',
        className
      )}
    >
      {children}
    </Component>
  );
};

export default ResponsiveText;
